import Dexie, { type EntityTable } from 'dexie';
import type {
  InventoryRecord,
  OrderCustomer,
  OrderItem,
  PickupLocation,
  QueuedOrder,
  SalesOrder,
  SalesPrice,
  ShipToAddress,
} from '@/types/domain';

export interface SyncMetadata {
  dataType: 'customers' | 'items' | 'inventory' | 'prices' | 'addresses' | 'locations' | 'orders';
  lastSyncAt: number;
  recordCount: number;
  status: 'success' | 'partial' | 'failed';
  errorMessage?: string;
}

class CarrfieldsDatabase extends Dexie {
  customers!: EntityTable<OrderCustomer, 'customer_no'>;
  items!: EntityTable<OrderItem, 'item_no'>;
  inventory!: EntityTable<InventoryRecord & { key: string }, 'key'>;
  prices!: EntityTable<SalesPrice & { key: string }, 'key'>;
  addresses!: EntityTable<ShipToAddress & { key: string }, 'key'>;
  locations!: EntityTable<PickupLocation, 'code'>;
  orders!: EntityTable<SalesOrder, 'orderId'>;
  queue!: EntityTable<QueuedOrder, 'queueId'>;
  syncMetadata!: EntityTable<SyncMetadata, 'dataType'>;

  constructor() {
    super('carrfields-pwa');
    this.version(1).stores({
      customers: '&customer_no, name, customer_price_group',
      items: '&item_no, description, vendor_no, is_third_party',
      inventory: '&key, itemNo, locationCode',
      prices: '&key, item_no, sales_code, sales_type',
      addresses: '&key, customer_no, code',
      orders: '&orderId, customerId, status, createdAt',
      queue: '&queueId, status, createdAt, retryCount',
      syncMetadata: '&dataType, lastSyncAt, status',
    });
    this.version(2).stores({
      locations: '&code, name',
    });
  }
}

export const db = new CarrfieldsDatabase();

// db.queue is deliberately excluded: it holds locally saved orders that haven't
// synced yet, not refetchable server cache. Clearing it would silently discard them.
export async function clearPrivateData(): Promise<void> {
  await db.transaction(
    'rw',
    [db.customers, db.items, db.inventory, db.prices, db.addresses, db.locations, db.orders, db.syncMetadata],
    async () => {
      await Promise.all([
        db.customers.clear(),
        db.items.clear(),
        db.inventory.clear(),
        db.prices.clear(),
        db.addresses.clear(),
        db.locations.clear(),
        db.orders.clear(),
        db.syncMetadata.clear(),
      ]);
    },
  );
}
